import { useEffect, useMemo, useState, type ComponentType, type JSX, type SVGProps } from 'react';
import { Volume2 as LucideVolume2, Volume1 as LucideVolume1, VolumeX as LucideVolumeX } from 'lucide-react';
import type { StartScreenConfig } from 'shared';
import { resolveMediaRefToUrl } from '../utils/mediaRef';
import { useAudio } from '../audio/useAudio';
import { getTimeOfDay } from '../atmosphere';
import { TsukumogamiCollection } from './collection/TsukumogamiCollection';

// P7-5: HUD の絵文字廃止。シェルは lucide-react を直接 import する流儀（RouteMapButton踏襲）。
type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;
const Volume2 = LucideVolume2 as unknown as IconComponent;
const Volume1 = LucideVolume1 as unknown as IconComponent;
const VolumeX = LucideVolumeX as unknown as IconComponent;

export interface StartScreenProps {
  config: StartScreenConfig;
  onStart: () => void;
  /** 保存された進行状況があるか */
  hasSavedProgress?: boolean;
  /** 続きからプレイを選択した時のコールバック */
  onResume?: () => void;
  /** 保存データの日時 */
  savedAt?: Date;
  /** 図鑑を現在の物語へ絞るためのシナリオ id。 */
  scenarioId?: string;
}

type AnimationPhase = 'initial' | 'logo' | 'title' | 'description' | 'button';

export function StartScreen({ config, onStart, hasSavedProgress, onResume, savedAt, scenarioId }: StartScreenProps): JSX.Element {
  const [phase, setPhase] = useState<AnimationPhase>('initial');
  // 没入 P5: 付喪神図鑑モーダルの開閉（StartScreen が own する＝シナリオ未プレイでも空状態を確認できる）。
  const [showCollection, setShowCollection] = useState(false);
  // R2 感覚層（Wave2A）: 音は全てグローバル音エンジンに統合。独自 HTMLAudio・独自 volume/mute state は廃止。
  const { muted, volume, setVolume, toggleMuted, playBgm, stopBgm, unlock } = useAudio();
  const animationMs = config.animationDurationMs ?? 1000;
  const showAnimation = config.showAnimation !== false;

  // P2「端末は付喪神」Wave B: 深夜帯（端末ローカル時刻）の判定。
  // マウント時点の時刻で固定（スタート画面の滞在中に跨いでも見た目が揺れない）。
  // 世界観 §7.4: 深夜は「気配が濃くなる時間帯」。ここでは(a)視覚トーンを少し暗く/寒色へ寄せ、
  // (b)九十九商会端末の “状態ログ断片”（monospace の診断表示・文ではなく状態）を 1 つだけ薄く出す。
  // この断片は端末の状態表示であって、システム付喪神（無言の現象）の台詞ではない。
  const isDeepNight = useMemo(() => getTimeOfDay().isDeepNight, []);

  // BGM 初期化: 解決済み URL をグローバル音エンジンへ渡すだけ。
  // unlock 前は engine 側が src だけ保持して鳴らず、初回ジェスチャ（AudioProvider が捕捉）で鳴り始める。
  // master mute/volume に自動連動。アンマウント時は停止し、ゲーム内 BGM へ重ならず引き継ぐ。
  useEffect(() => {
    if (!config.bgmAssetId) return;
    const bgmUrl = resolveMediaRefToUrl(config.bgmAssetId);
    if (!bgmUrl) return;

    playBgm(bgmUrl, { loop: true });

    return () => {
      stopBgm();
    };
  }, [config.bgmAssetId, playBgm, stopBgm]);

  // 画面クリックで unlock（自動再生がブロックされた場合のフォールバック）。
  // AudioProvider も初回ジェスチャを捕捉するが、ここでも明示的に呼んで確実に解錠する。
  const handleScreenClick = () => {
    unlock();
  };

  // ミュート切り替え（グローバル状態へ委譲）
  const handleToggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleMuted();
  };

  // 音量変更（グローバル状態へ委譲・上げたらミュート解除）
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (newVolume > 0 && muted) {
      toggleMuted();
    }
  };

  // 開始/続きから: ユーザー操作なので明示 unlock（確実に解錠）し、
  // タイトル BGM を止めてからゲームへ。ゲーム内 BGM と二重再生しないよう引き継ぐ。
  const handleStartClick = () => {
    unlock();
    stopBgm();
    onStart();
  };

  const handleResumeClick = () => {
    if (!onResume) return;
    unlock();
    stopBgm();
    onResume();
  };

  useEffect(() => {
    if (!showAnimation) {
      setPhase('button');
      return;
    }

    const phaseDelays: { phase: AnimationPhase; delay: number }[] = [
      { phase: 'logo', delay: 100 },
      { phase: 'title', delay: animationMs * 0.3 },
      { phase: 'description', delay: animationMs * 0.6 },
      { phase: 'button', delay: animationMs * 0.9 }
    ];

    const timers = phaseDelays.map(({ phase: p, delay }) =>
      setTimeout(() => setPhase(p), delay)
    );

    return () => timers.forEach(clearTimeout);
  }, [showAnimation, animationMs]);

  const getPhaseIndex = (p: AnimationPhase): number => {
    const order: AnimationPhase[] = ['initial', 'logo', 'title', 'description', 'button'];
    return order.indexOf(p);
  };

  const isVisible = (targetPhase: AnimationPhase): boolean => {
    return getPhaseIndex(phase) >= getPhaseIndex(targetPhase);
  };

  const fadeClass = (targetPhase: AnimationPhase): string => {
    if (!showAnimation) return 'start-screen__visible';
    return isVisible(targetPhase) ? 'start-screen__fade-in' : 'start-screen__hidden';
  };

  const backgroundUrl = config.backgroundAssetId
    ? resolveMediaRefToUrl(config.backgroundAssetId)
    : null;
  const logoUrl = config.logoAssetId
    ? resolveMediaRefToUrl(config.logoAssetId)
    : null;

  const theme = config.theme ?? 'dark';
  const themeClass = `start-screen--${theme}`;
  const deepNightClass = isDeepNight ? 'start-screen--deepnight' : '';

  return (
    <div
      className={`start-screen ${themeClass} ${deepNightClass}`.trim()}
      style={config.primaryColor ? { '--start-screen-primary': config.primaryColor } as React.CSSProperties : undefined}
      onClick={handleScreenClick}
    >
      {/* 音量コントロール（右上）。グローバル音エンジンへバインド */}
      {config.bgmAssetId && (
        <div className="start-screen__volume-control" onClick={(e) => e.stopPropagation()}>
          <button
            className="start-screen__volume-btn"
            onClick={handleToggleMute}
            aria-label={muted ? '音声オン' : '音声オフ'}
          >
            {(() => {
              const Icon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;
              return <Icon className="start-screen__volume-icon" aria-hidden="true" />;
            })()}
          </button>
          <input
            type="range"
            className="start-screen__volume-slider"
            min="0"
            max="1"
            step="0.05"
            value={muted ? 0 : volume}
            onChange={handleVolumeChange}
            aria-label="音量"
          />
        </div>
      )}

      {/* 背景画像 */}
      {backgroundUrl && (
        <div className="start-screen__background">
          <img src={backgroundUrl} alt="" />
          <div className="start-screen__background-overlay" />
        </div>
      )}

      {/* コンテンツ */}
      <div className="start-screen__content">
        {/* ロゴ */}
        {logoUrl && (
          <div className={`start-screen__logo ${fadeClass('logo')}`}>
            <img src={logoUrl} alt="" />
          </div>
        )}

        {/* タイトルエリア */}
        <div className={`start-screen__title-area ${fadeClass('title')}`}>
          <h1 className="start-screen__title">{config.title}</h1>
          {config.subtitle && (
            <p className="start-screen__subtitle">{config.subtitle}</p>
          )}
        </div>

        {/* 説明文 */}
        {config.description && (
          <div className={`start-screen__description ${fadeClass('description')}`}>
            <p>{config.description}</p>
          </div>
        )}

        {/* ボタンエリア */}
        <div className={`start-screen__button-area ${fadeClass('button')}`}>
          {/* 続きからボタン（保存データがある場合） */}
          {hasSavedProgress && onResume && (
            <div className="start-screen__resume-section">
              <button
                className="start-screen__start-button start-screen__resume-button"
                onClick={handleResumeClick}
              >
                続きから
              </button>
              {savedAt && (
                <p className="start-screen__saved-at">
                  前回: {savedAt.toLocaleDateString('ja-JP')} {savedAt.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
          )}
          {/* 最初からボタン */}
          <button
            className={`start-screen__start-button ${hasSavedProgress ? 'start-screen__new-game-button' : ''}`}
            onClick={handleStartClick}
          >
            {/* P8-3「シェルの声」: 既定値の英語 'START' を廃し世界内語彙へ
                （soma は JSON 側で同文言を明示指定済み。シナリオ指定があればそちらが勝つ）。 */}
            {hasSavedProgress ? '最初から' : (config.startButtonText || '端末を起動する')}
          </button>

          {/* 没入 P5: 図鑑導線。シナリオ未プレイでも空状態を確認できる。
              画面クリック(unlock)に巻き込まれないよう stopPropagation。 */}
          <button
            className="start-screen__collection-button"
            onClick={(e) => { e.stopPropagation(); setShowCollection(true); }}
          >
            図鑑
          </button>
        </div>

        {/* P2 Wave B: 深夜帯のみ、九十九商会端末の “状態ログ断片” を 1 つだけ薄く表示する。
            monospace の診断表示（文ではなく状態）であり、システム付喪神の台詞ではない（§7.4）。 */}
        {isDeepNight && (
          <div className="start-screen__device-log" aria-hidden="true">
            <span>[接続帯: 深夜 — 反応強]</span>
          </div>
        )}
      </div>

      {/* 没入 P5: 付喪神図鑑モーダル */}
      {showCollection && (
        <div onClick={(e) => e.stopPropagation()}>
          <TsukumogamiCollection onClose={() => setShowCollection(false)} scenarioId={scenarioId} />
        </div>
      )}
    </div>
  );
}

export default StartScreen;
